import useSWR from 'swr';
import toast from 'react-hot-toast';
import { Dispatch, useEffect } from 'react';
import API from './api';
import { useNavigate } from 'react-router';

interface AuthHookProps {
    middleware?: 'guest' | 'auth';
    redirectTo?: string;
    tmp: string;
}

interface LoginProps {
    values: {
        email: string;
        password: string;
    };
    setError: Dispatch<any>;
}

interface LoginUsingGoogleProps {
    credential: string;
}

interface ForgotPasswordProps {
    email: string;
}

interface RestPasswordProps {
    email: string;
    password: string;
    password_confirmation: string;
}

export const ROLE_SUPERADMIN = 'SUPERADMIN';
export const ROLE_ADMIN = 'ADMIN';
export const ROLE_USER = 'USER';

interface UserProps {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    vocative_name: string;
    email: string;
    role: 'SUPERADMIN' | 'ADMIN' | 'USER';
    is_institution_contact_person: boolean;
    avatar: string;
    onboarding_completed_at: string | null;
    features: string[];
    institution: {
        id: number;
        name: string;
        ico: string;
        full_address: string;
        street: string;
        city: string;
        postal_code: string;
        license: 'DEMO' | 'FULL' | 'TRIAL';
        has_pending_invoice: boolean;
        billing_email: string;
        demo_requested_at: string | null;
    };
}

export const useAuth = ({ middleware, redirectTo, tmp }: AuthHookProps) => {
    const navigate = useNavigate();

    const {
        data: user,
        mutate,
        isLoading,
    } = useSWR<UserProps>('/user', (url) =>
        API.get(url)
            .then((res) => {
                return res.data;
            })
            .catch((error) => {
                if (error.code === 'ECONNABORTED') return;

                if (middleware === 'auth') navigate('/'); // TODO: check

                throw error;
            })
    );

    const csrf = () => API.get('/sanctum/csrf-cookie');

    const login = async ({ values, setError }: LoginProps) => {
        setError(false);
        await csrf();

        return API.post('/login', values)
            .then((res) => {
                const activeLicenseType = res?.data?.user?.active_license_type;
                // @ts-ignore
                window.dataLayer?.push({
                    event: 'login',
                    userActiveLicenseType: activeLicenseType,
                });

                mutate().then(() => navigate('/dashboard'));
            })
            .catch((error) => {
                setError(error.response.data?.message);
            });
    };

    const loginUsingGoogle = async ({ credential }: LoginUsingGoogleProps) => {
        await csrf();

        return API.post('/auth/google/login', { credential })
            .then(() => mutate())
            .catch((error) => {
                toast.error(error.response.data?.message);
            });
    };

    const forgotPassword = async ({ email }: ForgotPasswordProps) => {
        await csrf();

        return API.post('/forgot-password', { email })
            .then((res) => {
                toast.success(res?.data?.status);
            })
            .catch((error) => {
                if (error.response.status !== 422) throw error;
            });
    };

    const resetPassword = async ({
        email,
        password,
        password_confirmation,
    }: RestPasswordProps) => {
        await csrf();

        return API.post('/reset-password', {
            email,
            password,
            password_confirmation,
        })
            .then((res) => {
                toast.success(res?.data?.status);
                // navigate('/');
            })
            .catch((error) => {
                if (error.response.status !== 422) throw error;
            });
    };

    const logout = async () => {
        return API.post('/logout')
            .then(() => {
                mutate(null).then(() => {
                    navigate('/');
                });
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    console.log(401);
                    return mutate(null);
                }

                throw error;
            });
    };

    useEffect(() => {
        // console.log(middleware, tmp);
        if (!middleware || isLoading) return;

        if (middleware === 'guest' && user) {
            navigate(redirectTo ?? '/dashboard');
            return;
        }

        if (middleware === 'auth' && !user) {
            navigate(redirectTo ?? '/');
            return;
        }
    }, [middleware, user, redirectTo, tmp, isLoading, navigate]);

    return {
        user,
        userHasFullLicense:
            user?.institution.license === 'FULL' ||
            user?.institution.license === 'TRIAL',
        isLoading,
        login,
        loginUsingGoogle,
        forgotPassword,
        resetPassword,
        logout,
    };
};
